import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bdAgentApi, leadsApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  TrendingUp, Zap, MessageSquare, BarChart3, RefreshCw,
  ArrowRight, Mail, Phone, Clock, AlertTriangle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const FOX_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/28ykjv9m_Transparent%20BG%20Fox.png';

const BDAgent = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('insights');
  const [insights, setInsights] = useState(null);
  const [scores, setScores] = useState(null);
  const [followups, setFollowups] = useState(null);
  const [outreach, setOutreach] = useState(null);
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState('');
  const [outreachType, setOutreachType] = useState('email');
  const [loading, setLoading] = useState({ insights: false, scores: false, followups: false, outreach: false });

  useEffect(() => {
    leadsApi.getAll().then(res => setLeads(res.data)).catch(() => {});
  }, []);

  const handleGetInsights = async () => {
    setLoading(l => ({ ...l, insights: true }));
    try {
      const res = await bdAgentApi.getPipelineInsights();
      setInsights(res.data);
    } catch (e) { toast.error('Failed to get insights'); }
    finally { setLoading(l => ({ ...l, insights: false })); }
  };

  const handleScoreLeads = async () => {
    setLoading(l => ({ ...l, scores: true }));
    try {
      const res = await bdAgentApi.scoreLeads();
      setScores(res.data);
      toast.success(`Scored ${res.data.leads_analyzed} leads`);
    } catch (e) { toast.error('Failed to score leads'); }
    finally { setLoading(l => ({ ...l, scores: false })); }
  };

  const handleGetFollowups = async () => {
    setLoading(l => ({ ...l, followups: true }));
    try {
      const res = await bdAgentApi.getFollowups();
      setFollowups(res.data);
    } catch (e) { toast.error('Failed to get follow-ups'); }
    finally { setLoading(l => ({ ...l, followups: false })); }
  };

  const handleDraftOutreach = async () => {
    if (!selectedLead) { toast.error('Select a lead first'); return; }
    setLoading(l => ({ ...l, outreach: true }));
    try {
      const res = await bdAgentApi.draftOutreach(selectedLead, outreachType);
      setOutreach(res.data);
    } catch (e) { toast.error('Failed to draft outreach'); }
    finally { setLoading(l => ({ ...l, outreach: false })); }
  };

  const tabs = [
    { id: 'insights', label: 'Pipeline Insights', icon: BarChart3 },
    { id: 'scoring', label: 'Lead Scoring', icon: Zap },
    { id: 'followups', label: 'Follow-ups', icon: Clock },
    { id: 'outreach', label: 'Outreach Drafts', icon: MessageSquare },
  ];

  const urgencyColor = (u) => u === 'high' ? 'bg-red-100 text-red-700' : u === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700';
  const priorityColor = (p) => p === 'high' ? 'bg-red-100 text-red-700' : p === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700';

  return (
    <div className="space-y-6" data-testid="bd-agent-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <img src={FOX_MASCOT} alt="BD Agent" className="w-14 h-14" />
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">BD Agent</h1>
          <p className="text-navy/60 mt-0.5">AI-powered business development insights and actions</p>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-2 border-b border-navy/10 pb-0">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px ${
              activeTab === tab.id
                ? 'border-[#40CFDE] text-[#1A2B5E]'
                : 'border-transparent text-navy/50 hover:text-navy/70'
            }`}
            data-testid={`bd-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Pipeline Insights */}
      {activeTab === 'insights' && (
        <div className="space-y-4" data-testid="bd-insights-tab">
          <div className="flex items-center justify-between">
            <p className="text-sm text-navy/60">AI analysis of your current pipeline health and recommendations.</p>
            <Button
              onClick={handleGetInsights}
              disabled={loading.insights}
              className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90"
              data-testid="get-insights-btn"
            >
              {loading.insights ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {loading.insights ? 'Analyzing...' : 'Analyze Pipeline'}
            </Button>
          </div>

          {insights && (
            <>
              {/* Metrics cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card className="border-navy/10"><CardContent className="p-5">
                  <p className="text-sm text-navy/60">Total Leads</p>
                  <p className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth'] mt-1">{insights.data?.total_leads || 0}</p>
                </CardContent></Card>
                <Card className="border-navy/10"><CardContent className="p-5">
                  <p className="text-sm text-navy/60">Active</p>
                  <p className="text-3xl font-bold text-[#106EFD] font-['Amaranth'] mt-1">{insights.data?.active_leads || 0}</p>
                </CardContent></Card>
                <Card className="border-navy/10"><CardContent className="p-5">
                  <p className="text-sm text-navy/60">Pipeline Value</p>
                  <p className="text-3xl font-bold text-[#37C563] font-['Amaranth'] mt-1">${(insights.data?.total_pipeline_value || 0).toLocaleString()}</p>
                </CardContent></Card>
                <Card className="border-navy/10"><CardContent className="p-5">
                  <p className="text-sm text-navy/60">Stale Leads</p>
                  <p className="text-3xl font-bold text-red-500 font-['Amaranth'] mt-1">{insights.data?.stale_leads || 0}</p>
                </CardContent></Card>
              </div>

              {/* AI Insights */}
              <Card className="border-navy/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-5 h-5 text-[#40CFDE]" />
                    <h3 className="font-semibold text-[#1A2B5E]">AI Analysis</h3>
                  </div>
                  <div className="prose prose-sm max-w-none text-navy/70 whitespace-pre-wrap" data-testid="insights-content">
                    {insights.insights}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {!insights && !loading.insights && (
            <Card className="border-navy/10 border-dashed">
              <CardContent className="py-16 text-center">
                <BarChart3 className="w-12 h-12 mx-auto text-navy/20 mb-3" />
                <p className="text-navy/50">Click "Analyze Pipeline" to get AI-powered insights</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Lead Scoring */}
      {activeTab === 'scoring' && (
        <div className="space-y-4" data-testid="bd-scoring-tab">
          <div className="flex items-center justify-between">
            <p className="text-sm text-navy/60">AI scores your active leads based on engagement, value, and pipeline fit.</p>
            <Button
              onClick={handleScoreLeads}
              disabled={loading.scores}
              className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90"
              data-testid="score-leads-btn"
            >
              {loading.scores ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              {loading.scores ? 'Scoring...' : 'Score Leads'}
            </Button>
          </div>

          {scores?.scores?.length > 0 ? (
            <div className="space-y-3">
              {scores.scores.map((s, i) => (
                <Card key={s.id || i} className="border-navy/10 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/leads/${s.id}`)}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#40CFDE] to-[#106EFD] flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
                      {s.score}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-[#1A2B5E]">{s.company || s.id}</h4>
                        <Badge className={priorityColor(s.priority)}>{s.priority}</Badge>
                      </div>
                      <p className="text-sm text-navy/60 mt-0.5">{s.reasoning}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-navy/30 flex-shrink-0" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : !loading.scores ? (
            <Card className="border-navy/10 border-dashed">
              <CardContent className="py-16 text-center">
                <Zap className="w-12 h-12 mx-auto text-navy/20 mb-3" />
                <p className="text-navy/50">Click "Score Leads" to get AI priority scores</p>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      {/* Follow-ups */}
      {activeTab === 'followups' && (
        <div className="space-y-4" data-testid="bd-followups-tab">
          <div className="flex items-center justify-between">
            <p className="text-sm text-navy/60">AI identifies leads needing follow-up and suggests specific actions.</p>
            <Button
              onClick={handleGetFollowups}
              disabled={loading.followups}
              className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90"
              data-testid="get-followups-btn"
            >
              {loading.followups ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
              {loading.followups ? 'Analyzing...' : 'Find Follow-ups'}
            </Button>
          </div>

          {followups?.followups?.length > 0 ? (
            <div className="space-y-3">
              {followups.followups.map((f, i) => (
                <Card key={f.id || i} className="border-navy/10">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-[#1A2B5E]">{f.company}</h4>
                          <Badge className={urgencyColor(f.urgency)}>{f.urgency}</Badge>
                          {f.days_inactive && <span className="text-xs text-navy/40">{f.days_inactive}d inactive</span>}
                        </div>
                        <p className="text-sm text-navy/60 mt-1">{f.reasoning}</p>
                        <div className="mt-2 p-2.5 bg-[#40CFDE]/10 rounded-lg">
                          <p className="text-sm font-medium text-[#1A2B5E]">Suggested action:</p>
                          <p className="text-sm text-navy/70">{f.suggested_action}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/leads/${f.id}`)} data-testid={`view-lead-${f.id}`}>
                        View Lead
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : followups?.followups?.length === 0 ? (
            <Card className="border-navy/10">
              <CardContent className="py-12 text-center">
                <Check className="w-12 h-12 mx-auto text-green-400 mb-3" />
                <p className="text-navy/60 font-medium">All leads are up to date!</p>
                <p className="text-sm text-navy/40 mt-1">No follow-ups needed right now.</p>
              </CardContent>
            </Card>
          ) : !loading.followups ? (
            <Card className="border-navy/10 border-dashed">
              <CardContent className="py-16 text-center">
                <Clock className="w-12 h-12 mx-auto text-navy/20 mb-3" />
                <p className="text-navy/50">Click "Find Follow-ups" to identify leads needing attention</p>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      {/* Outreach Drafts */}
      {activeTab === 'outreach' && (
        <div className="space-y-4" data-testid="bd-outreach-tab">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div className="flex-1 space-y-2">
              <p className="text-sm text-navy/60">AI drafts personalized outreach for any lead.</p>
              <div className="flex gap-3">
                <Select value={selectedLead || 'none'} onValueChange={v => setSelectedLead(v === 'none' ? '' : v)}>
                  <SelectTrigger className="w-[280px]" data-testid="outreach-lead-select">
                    <SelectValue placeholder="Select a lead..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a lead...</SelectItem>
                    {leads.filter(l => l.stage !== 'closed' && l.stage !== 'abandoned').map(l => (
                      <SelectItem key={l.id} value={l.id}>{l.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={outreachType} onValueChange={setOutreachType}>
                  <SelectTrigger className="w-[140px]" data-testid="outreach-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="text">Text/SMS</SelectItem>
                    <SelectItem value="call_script">Call Script</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              onClick={handleDraftOutreach}
              disabled={loading.outreach || !selectedLead}
              className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90"
              data-testid="draft-outreach-btn"
            >
              {loading.outreach ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
              {loading.outreach ? 'Drafting...' : 'Draft Outreach'}
            </Button>
          </div>

          {outreach?.draft ? (
            <Card className="border-navy/10">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-5 h-5 text-[#106EFD]" />
                  <h3 className="font-semibold text-[#1A2B5E]">
                    {outreachType === 'email' ? 'Email Draft' : outreachType === 'text' ? 'SMS Draft' : 'Call Script'}
                  </h3>
                  <Badge variant="outline" className="text-xs">AI Generated</Badge>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-sm text-navy/80" data-testid="outreach-content">
                  {outreach.draft}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(outreach.draft).then(() => toast.success('Copied!'))}>
                    Copy to clipboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : !loading.outreach ? (
            <Card className="border-navy/10 border-dashed">
              <CardContent className="py-16 text-center">
                <MessageSquare className="w-12 h-12 mx-auto text-navy/20 mb-3" />
                <p className="text-navy/50">Select a lead and click "Draft Outreach" to generate personalized messaging</p>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}
    </div>
  );
};

// Need Check icon for empty followups state
const Check = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default BDAgent;
